import React, { useState, useEffect, useRef } from "react";
import { Input, Spin } from "antd";
import { useTranslation } from "react-i18next";
import axios from "axios";

const LockScreenModal = ({ visible: propVisible, onUnlock }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:4000";
  const [isModalVisible, setIsModalVisible] = useState(false); // Local visibility control
  const inactivityTimeoutRef = useRef(null);
  const INACTIVITY_DELAY = 3000; // 3 seconds to restart animation
  const [isAnimating, setIsAnimating] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const isLocked = localStorage.getItem("isLocked") === "true";
    const shouldShowModal = propVisible || isLocked;
    setIsModalVisible(shouldShowModal);
    if (shouldShowModal) {
      setPassword("");
      setError("");
      setLoading(false);
      setIsAnimating(true); // Start animation immediately when visible
      setIsHovered(false);
    } else {
      clearTimeout(inactivityTimeoutRef.current); // Clean up when hidden
    }
  }, [propVisible]);

  const startInactivityTimer = () => {
    clearTimeout(inactivityTimeoutRef.current);
    inactivityTimeoutRef.current = setTimeout(() => {
      if (!isHovered) {
        setIsAnimating(true);
      }
    }, INACTIVITY_DELAY);
  };

  const handleSubmit = async () => {
    if (!password || password.trim() === "") {
      setError(t("password_required"));
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError(t("authentication_error"));
        return;
      }

      const response = await axios.post(
        `${apiUrl}/users/validate-lock`,
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setError("");
        setPassword("");
        localStorage.removeItem("isLocked");
        setIsModalVisible(false);
        onUnlock();
      } else {
        setError(response.data.message || t("incorrect_password"));
      }
    } catch (error) {
      setError(t("validation_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    setIsAnimating(false);
    clearTimeout(inactivityTimeoutRef.current);
    startInactivityTimer();
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const handleInputChange = (e) => {
    setPassword(e.target.value);
    setIsAnimating(false);
    clearTimeout(inactivityTimeoutRef.current);
    startInactivityTimer();
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    setIsAnimating(false);
    clearTimeout(inactivityTimeoutRef.current);
    // Do not start timer while hovered
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    startInactivityTimer();
  };

  const handleMouseMove = () => {
    setIsAnimating(false);
    clearTimeout(inactivityTimeoutRef.current);
    // Do not start timer while hovered
  };

  if (!isModalVisible) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 9999,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      aria-label="Lock Screen Modal"
      onKeyDown={handleKeyPress} // Capture key events on the overlay
    >
      <div
        style={{
          background: "linear-gradient(135deg, #283593, #1e88e5)",
          borderRadius: "12px",
          padding: "30px",
          width: "400px",
          textAlign: "center",
          color: "#ffffff",
          boxShadow: "0 4px 12px rgba(30, 136, 229, 0.3), 0 0 10px rgba(30, 136, 229, 0.4)", // Final shadow state
          position: "relative",
          fontFamily: "'Roboto', sans-serif",
          transform: "scale(1)", // Base scale
          opacity: 1, // Base opacity
          animation: isAnimating ? "zoomInSmooth 8s ease-out infinite" : "none",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
      >
        <button
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            background: "transparent",
            border: "none",
            color: "#ffffff",
            fontSize: "20px",
            cursor: "not-allowed",
            opacity: 0.5,
          }}
          disabled
          aria-hidden="true"
        >
          ×
        </button>

        <img
          src="https://netshine-assets.blr1.cdn.digitaloceanspaces.com/web-assets/logo-dark.png"
          alt="Netshine Office"
          style={{
            width: "200px",
            marginBottom: "20px",
            filter: "brightness(1.2)", // Subtle glow
          }}
        />

        <h2
          style={{
            fontSize: "21px",
            fontWeight: "700",
            marginBottom: "20px",
            color: "#ffffff",
            textShadow: "0 1px 3px rgba(0, 0, 0, 0.5)",
          }}
        >
          {t("enter_password_to_unlock")}
        </h2>

        <Input.Password
          value={password}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          placeholder={t("password")}
          style={{
            marginBottom: "10px",
            height: "38px",
            borderRadius: "8px",
            background: "#ffffff",
            color: "#333",
            fontSize: "16px",
          }}
          disabled={loading}
          autoFocus
          aria-label="Enter lock password"
        />

        {error && (
          <div
            style={{
              color: "#ff6b6b",
              marginBottom: "10px",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          style={{
            backgroundColor: "#00e676",
            color: "#000000",
            fontWeight: "600",
            padding: "12px 25px",
            borderRadius: "8px",
            border: "none",
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: "16px",
            transition: "background 0.3s ease, transform 0.2s ease",
            width: "100%",
            opacity: loading ? 0.7 : 1,
            fontFamily: "'Roboto', sans-serif",
            boxShadow: "0 0 10px rgba(0, 230, 118, 0.5)", // Persistent glow
          }}
          disabled={loading}
          aria-label="Unlock Application"
          onMouseOver={(e) => {
            if (!loading) e.target.style.transform = "scale(1.05)";
          }}
          onMouseOut={(e) => {
            if (!loading) e.target.style.transform = "scale(1)";
          }}
        >
          {loading ? <Spin size="small" /> : t("unlock")}
        </button>
      </div>
      <style jsx>{`
        @keyframes zoomInSmooth {
          0% {
            transform: scale(0.5);
            opacity: 0;
            box-shadow: 0 0 2px rgba(30, 136, 229, 0.2);
          }
          1.875% {
            transform: scale(0.6);
            opacity: 0.2;
            box-shadow: 0 0 4px rgba(30, 136, 229, 0.25);
          }
          3.75% {
            transform: scale(0.7);
            opacity: 0.4;
            box-shadow: 0 2px 6px rgba(30, 136, 229, 0.3);
          }
          5.625% {
            transform: scale(0.8);
            opacity: 0.5;
            box-shadow: 0 4px 8px rgba(30, 136, 229, 0.35);
          }
          7.5% {
            transform: scale(0.9);
            opacity: 0.65;
            box-shadow: 0 6px 10px rgba(30, 136, 229, 0.4);
          }
          9.375% {
            transform: scale(1.05);
            opacity: 0.85;
            box-shadow: 0 8px 15px rgba(30, 136, 229, 0.45);
          }
          11.25% {
            transform: scale(1.08);
            opacity: 0.95;
            box-shadow: 0 10px 18px rgba(30, 136, 229, 0.5);
          }
          13.125% {
            transform: scale(1.1);
            opacity: 1;
            box-shadow: 0 12px 20px rgba(30, 136, 229, 0.55);
          }
          15% {
            transform: scale(1.05);
            opacity: 1;
            box-shadow: 0 10px 18px rgba(30, 136, 229, 0.5);
          }
          16.875% {
            transform: scale(1.02);
            opacity: 1;
            box-shadow: 0 8px 15px rgba(30, 136, 229, 0.4);
          }
          18.75% {
            transform: scale(1);
            opacity: 1;
            box-shadow: 0 4px 12px rgba(30, 136, 229, 0.3), 0 0 10px rgba(30, 136, 229, 0.4);
          }
          81.25% {
            transform: scale(1);
            opacity: 1;
            box-shadow: 0 4px 12px rgba(30, 136, 229, 0.3), 0 0 10px rgba(30, 136, 229, 0.4);
          }
          83.125% {
            transform: scale(1.02);
            opacity: 1;
            box-shadow: 0 8px 15px rgba(30, 136, 229, 0.4);
          }
          85% {
            transform: scale(1.05);
            opacity: 1;
            box-shadow: 0 10px 18px rgba(30, 136, 229, 0.5);
          }
          86.875% {
            transform: scale(1.1);
            opacity: 1;
            box-shadow: 0 12px 20px rgba(30, 136, 229, 0.55);
          }
          88.75% {
            transform: scale(1.08);
            opacity: 0.95;
            box-shadow: 0 10px 18px rgba(30, 136, 229, 0.5);
          }
          90.625% {
            transform: scale(1.05);
            opacity: 0.85;
            box-shadow: 0 8px 15px rgba(30, 136, 229, 0.45);
          }
          92.5% {
            transform: scale(0.9);
            opacity: 0.65;
            box-shadow: 0 6px 10px rgba(30, 136, 229, 0.4);
          }
          94.375% {
            transform: scale(0.8);
            opacity: 0.5;
            box-shadow: 0 4px 8px rgba(30, 136, 229, 0.35);
          }
          96.25% {
            transform: scale(0.7);
            opacity: 0.4;
            box-shadow: 0 2px 6px rgba(30, 136, 229, 0.3);
          }
          98.125% {
            transform: scale(0.6);
            opacity: 0.2;
            box-shadow: 0 0 4px rgba(30, 136, 229, 0.25);
          }
          100% {
            transform: scale(0.5);
            opacity: 0;
            box-shadow: 0 0 2px rgba(30, 136, 229, 0.2);
          }
        }
      `}</style>
    </div>
  );
};

export default LockScreenModal;